import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import {
  type Application,
  type ApplicationAccessList,
  type OrganizationRoleShort,
  applicationsApplicationsAccessListOptions,
  applicationsApplicationsListOptions
} from 'src/api/main'

export type UserAppAccessRowData = {
  application: Application
  granted: boolean
  accessId: ApplicationAccessList['id'] | null
  roles: Array<OrganizationRoleShort>
}

type Args = {
  workspaceId: number
  userId: number
  enabled?: boolean
}

export const useUserAppAccess = ({
  workspaceId,
  userId,
  enabled = true
}: Args) => {
  const ready =
    enabled && Number.isFinite(workspaceId) && Number.isFinite(userId)

  const appsQuery = useQuery({
    ...applicationsApplicationsListOptions({
      query: { organization: workspaceId, per_page: 200 }
    }),
    enabled: ready
  })

  const accessQuery = useQuery({
    ...applicationsApplicationsAccessListOptions({
      query: { organization: workspaceId, user: userId, per_page: 200 }
    }),
    enabled: ready
  })

  const rows = useMemo<UserAppAccessRowData[]>(() => {
    const apps = appsQuery.data?.results ?? []
    const access = accessQuery.data?.results ?? []
    const accessByApp = new Map<number, ApplicationAccessList>()
    for (const a of access) accessByApp.set(a.application.id, a)

    return apps.map((application) => {
      const match = accessByApp.get(application.id)

      return {
        application,
        granted: Boolean(match),
        accessId: match?.id ?? null,
        roles: match?.roles ?? []
      }
    })
  }, [appsQuery.data, accessQuery.data])

  return {
    rows,
    grantedCount: rows.filter((r) => r.granted).length,
    isLoading: appsQuery.isLoading || accessQuery.isLoading,
    isError: appsQuery.isError || accessQuery.isError
  }
}
